import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import { Avatar, SideNav } from "@performyard/py-components";

import TopBarPortal from "./TopBarPortal";

import { useNavigationConfig } from "../../api/hooks/defs/navigation";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";
import { useMobileSidenav } from "./useMobileSidenav";

import { isEmbeddedReferenceFrame } from "../../utils/isEmbeddedReferenceFrame";

import { isAdminRoute, isNoSidebarRoute, isSidebarVisibleRoute } from "./layout";
import { calculateSideNavConfig, findActiveSideNavKey } from "./side-nav-config";
import { SideNavLogo } from "./sideNavLogo";
import { SIDE_NAV_PRIMARY_BLUE_THEME } from "./theme";
import { buildUserMenuOptions } from "./userMenuOptions";

// admin/base.css has unscoped `!important` rules that leak across SPA hops; strip
// it on non-admin routes. Going to admin always fullPageLoads, so it reloads.
// Pinned to a regex so build-output renames (e.g. hashed filenames like
// `administration/base.abc123.css`) still match the intended asset.
const ADMIN_STYLESHEET_HREF_PATTERN = /\/administration\/base[.-][^/]*\.css($|\?)/;

const removeAdminStylesheets = () => {
	if (typeof document === "undefined") return;
	const links = document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]');
	links.forEach((link) => {
		if (ADMIN_STYLESHEET_HREF_PATTERN.test(link.href)) {
			link.remove();
		}
	});
};

const SideNavShell = () => {
	const { data, isLoading } = useNavigationConfig(undefined, "viewer");
	const { value: isInAppReferralsEnabled } = useFeatureFlagQuery("ff-is-in-app-referrals-enabled", false);
	const location = useLocation();
	const mobileSidenav = useMobileSidenav();

	useEffect(() => {
		if (!isAdminRoute(location.pathname)) removeAdminStylesheets();
	}, [location.pathname]);

	useEffect(() => {
		if (typeof document === "undefined") return;
		// Server suppresses chrome by leaving `data-layout` unset (iframe pages,
		// public survey-taker, etc.); don't flip `data-sidenav-visible` then.
		if (document.body.dataset.layout !== "sidenav") return;
		if (isEmbeddedReferenceFrame()) {
			document.body.dataset.sidenavVisible = "false";
			return;
		}
		document.body.dataset.sidenavVisible = isSidebarVisibleRoute(location.pathname) ? "true" : "false";
	}, [location.pathname]);

	const config = data?.navigationConfig ?? null;

	const items = useMemo(() => {
		if (!config) return [];
		return calculateSideNavConfig(config);
	}, [config]);

	const activeKey = useMemo(() => {
		if (items.length === 0) return null;
		return findActiveSideNavKey(items, location.pathname, location.search);
	}, [items, location.pathname, location.search]);

	const userMenuOptions = useMemo(
		() => (config ? buildUserMenuOptions(config, { isInAppReferralsEnabled }) : undefined),
		[config, isInAppReferralsEnabled]
	);

	const sideNavMount = typeof document !== "undefined" ? document.getElementById("side-nav") : null;

	if (isLoading || !config || config.preferredNavigation !== "new" || !sideNavMount) return null;

	if (isEmbeddedReferenceFrame()) return null;

	const user = {
		id: config.currentUserId,
		name: config.currentUserName,
		role:
			config.currentUserJobTitle || (config.isAdmin ? "Admin" : config.hasDirectReports ? "Manager" : "Employee"),
		email: config.currentUserEmail || "",
	};

	const avatar = <Avatar size={48} id={config.currentUserId} name={config.currentUserName || "User"} />;
	const logo = <SideNavLogo config={config} />;
	const sideNavTheme = config.sideNavTheme ?? SIDE_NAV_PRIMARY_BLUE_THEME;

	return (
		<>
			{isNoSidebarRoute(location.pathname)
				? null
				: createPortal(
						<SideNav
							id="side-nav-panel"
							items={items}
							activeKey={activeKey}
							user={user}
							theme={sideNavTheme}
							logo={logo}
							avatar={avatar}
							userMenuOptions={userMenuOptions}
							onClose={mobileSidenav.close}
							isMobileOverlay={mobileSidenav.isOpen}
						/>,
						sideNavMount
					)}
			<TopBarPortal
				config={config}
				onToggleMobileSidenav={mobileSidenav.toggle}
				isMobileSidenavOpen={mobileSidenav.isOpen}
			/>
		</>
	);
};

export default SideNavShell;
